<template>
  <div class="global-alert-container">

    <transition-group name="fade-slide" tag="div">

      <div
        v-for="alert in alerts"
        :key="alert.id"
        class="alert alert-dismissible fade show font-weight-light shadow-lg"
        :class="alert.bgColor"
        role="alert"
      >

        <div class="alert-content">

          <span class="alert-icon">
            <span class="material-symbols-rounded">
              {{ alert.icon }}
            </span>
          </span>

          <span class="alert-text">
            {{ alert.message }}
          </span>

        </div>

        <button
          type="button"
          class="btn-close d-flex justify-content-center align-items-center ms-2"
          @click="hideAlert(alert.id)"
          aria-label="Close"
        >
          <span
            aria-hidden="true"
            class="text-lg font-weight-bold"
          >
            ×
          </span>
        </button>

      </div>

    </transition-group>

  </div>
</template>

<script>
import emitter from "@/utils/emitter";

export default {
  name: "GlobalAlert",

  data() {
    return {
      alerts: [],
      nextId: 1
    };
  },

  mounted() {
    emitter.on("trigger-error", this.showAlert);
  },

  beforeUnmount() {
    emitter.off("trigger-error", this.showAlert);

    this.alerts.forEach(alert => {
      if (alert.timeoutId) {
        clearTimeout(alert.timeoutId);
      }
    });
  },

  methods: {

    showAlert(payload) {

      const id = this.nextId++;

      const alert = {
        id,
        message: payload.message,
        bgColor: payload.bgColor || "alert-primary",
        icon: payload.icon || "error",
        duration: payload.duration ?? payload.time ?? 5000,
        timeoutId: null
      };

      this.alerts.push(alert);

      // Automatically remove this alert
      // after its own duration.
      if (alert.duration !== null) {

        alert.timeoutId = setTimeout(() => {
          this.hideAlert(id);
        }, alert.duration);
      }
    },

    hideAlert(id) {

      const index = this.alerts.findIndex(
        alert => alert.id === id
      );

      if (index === -1)
        return;

      const alert = this.alerts[index];

      if (alert.timeoutId) {
        clearTimeout(alert.timeoutId);
      }

      this.alerts.splice(index, 1);

      this.$emit("close");
    }
  }
};
</script>

<style scoped>

/* Alert container */

.global-alert-container {
  position: fixed;
  top: 3rem;
  right: 1rem;
  z-index: 1050;

  width: 600px;
  max-width: calc(100vw - 2rem);
}

/* Individual alert */

.alert {
  width: 100%;
  min-width: 300px;
  max-width: 600px;

  margin-bottom: 0.75rem;

  display: flex;
  align-items: center;
  justify-content: space-between;

  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;

  border-radius: 0.375rem;

  transition: opacity 0.5s ease;
}


/* Alert content */
.alert-content {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;

  min-width: 0;
  flex: 1;
}

.alert-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding-top: 2px;
}

.alert-text {
  min-width: 0;

  color: #ffffff !important;
  font-weight: 700;
  
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;

  line-height: 1.4;
}


/* ---------------------------------------------------------
   Close button
   --------------------------------------------------------- */

.btn-close {
  flex-shrink: 0;
}


/* ---------------------------------------------------------
   Alert colours
   --------------------------------------------------------- */

.alert-primary {
  color: #084298;
  background-color: #cfe2ff;
  border: 1px solid #b6d4fe;
}

.alert-danger {
  color: #842029;
  background-color: #f8d7da;
  border: 1px solid #f5c2c7;
}

.alert-success {
  color: #0f5132;
  background-color: #d1e7dd;
  border: 1px solid #badbcc;
}

.alert-warning {
  color: #663c00;
  background-color: #f59e0b;
  border: 1px solid hsl(19, 79%, 56%);
}


/* ---------------------------------------------------------
   Material icons
   --------------------------------------------------------- */

.material-symbols-rounded {
  font-size: 20px;
  line-height: 1;
  vertical-align: middle;
}


/* ---------------------------------------------------------
   Transition
   --------------------------------------------------------- */

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.35s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

.fade-slide-enter-to,
.fade-slide-leave-from {
  opacity: 1;
  transform: translateY(0);
}


/* ---------------------------------------------------------
   Mobile
   --------------------------------------------------------- */

@media (max-width: 768px) {

  .global-alert-container {
    top: 1rem;
    right: 0.75rem;
    left: 0.75rem;

    width: auto;
    max-width: none;
  }

  .alert {
    min-width: 0;
    max-width: none;
  }
}

</style>